import React from 'react';
import { PANEL_PADDING_X, ROW_PADDING_Y } from '../../utils/layout';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete as DeleteIcon, Edit as EditIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import IconAction from './IconAction';

/**
 * A card containing rows of records. Each row is one big click target that
 * opens the record; meta chips and edit/delete actions sit at the right edge
 * as flex siblings, so they never overlap the row content.
 */
export const EntityList = ({ children, ...props }) => (
  <Paper {...props}>
    <List disablePadding>
      {React.Children.toArray(children)
        .filter(Boolean)
        .map((child, index) => (
          <React.Fragment key={child.key ?? index}>
            {index > 0 && <Divider component="li" />}
            {child}
          </React.Fragment>
        ))}
    </List>
  </Paper>
);

/**
 * Edit / delete buttons for a row. The tooltips are the short action
 * ("Edit", "Delete"); pass `name` (e.g. "transect 2") so screen readers hear
 * which record. `onEdit` or `editTo` (a route) for edit. Pass
 * `deleteDisabledReason` to show delete greyed out with that explanation
 * instead of hiding it.
 */
export const RowActions = ({ onEdit, editTo, onDelete, name, editLabel = 'Edit', deleteLabel = 'Delete', deleteDisabledReason }) => (
  <Stack direction="row" spacing={0.5}>
    {(onEdit || editTo) && (
      <IconAction
        label={editLabel}
        ariaLabel={name ? `${editLabel} ${name}` : undefined}
        size="small"
        icon={<EditIcon fontSize="small" />}
        {...(editTo ? { component: RouterLink, to: editTo } : { onClick: onEdit })}
      />
    )}
    {onDelete && (
      <IconAction
        label={deleteLabel}
        ariaLabel={name ? `${deleteLabel} ${name}` : undefined}
        size="small"
        color="error"
        disabledReason={deleteDisabledReason}
        onClick={onDelete}
        icon={<DeleteIcon fontSize="small" />}
      />
    )}
  </Stack>
);

export const EntityRow = ({ primary, secondary, meta, to, onClick, actions, leading, highlight = false }) => {
  const clickable = Boolean(to || onClick);
  const buttonProps = to ? { component: RouterLink, to } : { onClick };

  const body = (
    <>
      {leading && <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>{leading}</Box>}
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{ fontWeight: 600, component: 'div' }}
        secondaryTypographyProps={{ component: 'div', sx: { mt: 0.25 } }}
        sx={{ my: 0, minWidth: 0 }}
      />
      {clickable && !actions && !meta && (
        <ChevronRightIcon sx={{ ml: 1, color: 'text.disabled', flexShrink: 0 }} fontSize="small" />
      )}
    </>
  );

  return (
    <ListItem
      disablePadding
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pr: meta || actions ? 1.5 : 0,
        bgcolor: (theme) => (highlight ? alpha(theme.palette.primary.main, 0.08) : 'transparent'),
        transition: 'background-color 600ms ease',
      }}
    >
      {clickable ? (
        <ListItemButton {...buttonProps} sx={{ py: ROW_PADDING_Y, px: PANEL_PADDING_X, minWidth: 0 }}>
          {body}
        </ListItemButton>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0, py: ROW_PADDING_Y, px: PANEL_PADDING_X }}>
          {body}
        </Box>
      )}
      {meta && (
        <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
          {meta}
        </Box>
      )}
      {actions && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{actions}</Box>}
    </ListItem>
  );
};

/** Small inline "label · value" pieces for a row's secondary line. */
export const RowMeta = ({ parts }) => (
  <Typography variant="body2" color="text.secondary" component="span">
    {parts.filter(Boolean).join('  ·  ')}
  </Typography>
);
